import { useMemo } from "react";

// first, ..., prev, current, next, ..., last
const MINIMAL_PAGE_ITEM_COUNT = 7;

interface MakePaginationOption {
  total: number;
  width: number;
}

type Items = (number | "next-more" | "prev-more")[];

export const makePaginationIndex = (
  current: number,
  option: MakePaginationOption,
): Items => {
  const { width, total } = option;
  if (width < MINIMAL_PAGE_ITEM_COUNT) {
    throw new Error(
      `Must allow at least ${MINIMAL_PAGE_ITEM_COUNT} page items`,
    );
  }
  if (width % 2 === 0) {
    throw new Error(`Must allow odd number of page items`);
  }
  if (total < width) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const left = Math.max(
    0,
    Math.min(total - width, current - Math.floor(width / 2)),
  );
  const items: number[] = Array.from({ length: width }, (_, i) => i + left);
  const result: Items = items.slice();
  if (items[0] > 0) {
    result[0] = 0;
    result[1] = "prev-more";
  }
  if (items[items.length - 1] < total - 1) {
    result[items.length - 1] = total - 1;
    result[items.length - 2] = "next-more";
  }
  return result;
};

export const usePaginationIndex = (
  current: number,
  option: MakePaginationOption,
) => {
  const result = useMemo(() => {
    return makePaginationIndex(current, option);
  }, [current, option]);
  return result;
};
